import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo
} from "react";

import AceEditor from "react-ace";
import Ace from "ace-builds";
import "ace-builds/src-noconflict/mode-abc";
import "ace-builds/src-noconflict/theme-xcode";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/pro-light-svg-icons";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";

import Exsurge, * as exsurge from "react-exsurge";

import MouseEventUtils from "../js/MouseEventUtils";
import findCommonAncestor, { parents } from "../js/findCommonAncestor";
import TextSelection from "../js/TextSelection";
import ChantDocument from "../js/ChantDocument";
import { ReactComponent as SvgDefs } from "../svg/defs.svg";
import { gabcAceCustomCommands } from "../utils/ace-custom-commands";
import "../scss/EditorPane.scss";
import "../scss/Exsurge.scss";

/**
 * 
 * @param {exsurge.ChantContext} ctxt 
 */
const contextCreated = (ctxt) => {
  ctxt.specialCharMap['*'] = exsurge.greextraGlyphs.StarSix;
  ctxt.specialCharMap['+'] = exsurge.greextraGlyphs.Dagger;
  ctxt.asteriskProperties["font-family"] = `greextra`;
  ctxt.plusProperties["font-family"] = `greextra`;
}


function EditorPane(props) {
  const [showingCode, setShowingCode] = useState(true);
  const scoreRef = useRef();
  const aceRef = useRef();
  const gabcHeaderLenRef = useRef(0);
  const doc = props.document,
    { onChangeFilename } = props;
  const showHeader = false;

  const handleFileNameKeyDown = useCallback(
    e => {
      if (e.key === "Escape" && !(e.altKey || e.metaKey || e.ctrlKey)) {
        e.target.value = doc.name;
        e.target.blur();
      } else if (e.key === "Enter") {
        onChangeFilename(e.target.value);
        e.target.blur();
      }
    },
    [doc.name, onChangeFilename]
  );

  const setGabcSourceSelection = useCallback((start, length) => {
    if (aceRef.current) {
      const ace = aceRef.current.editor,
        document = ace.getSession().getDocument(),
        startRange = document.indexToPosition(start),
        endRange = length
          ? document.indexToPosition(start + length)
          : startRange;
      ace.selection.setSelectionRange(
        new Ace.Range(
          startRange.row,
          startRange.column,
          endRange.row,
          endRange.column
        )
      );
      aceRef.current.editor.focus();
    }
  }, []);

  const noop = useCallback(() => {}, []);
  const exsurgeSelection = props.exsurgeSelection,
    setExsurgeSelection = props.onUpdateExsurgeSelection || noop,
    elementSelection = (exsurgeSelection && exsurgeSelection.element) || null,
    selectedIndices = (elementSelection && elementSelection.indices) || null;

  const { onUpdateNoteGabc, onUndoDocumentChange } = props;

  const updateNoteGabcForTextOnly = useCallback(
    (nextIndex, nextNote, updateSelection = true) => {
      const score = scoreRef.current;
      const lyricText = nextNote.lyrics?.[0]?.text;
      const firstSpace = lyricText?.indexOf(" ") ?? -1;
      if (firstSpace > 0) {
        onUpdateNoteGabc(
          [],
          "",
          { inLyricOfElementIndex: nextIndex },
          { lyricOffset: firstSpace }
        );
      } else if (lyricText && score.notes.length === nextIndex + 1) {
        // last note, so we need to call onUpdateNoteGabc just in case it doesn't even have parentheses at all:
        onUpdateNoteGabc(
          [],
          "",
          { inLyricOfElementIndex: nextIndex },
          { lyricOffset: lyricText.length }
        );
      }
      if (updateSelection) {
        let newSelection = {
          element: {
            indices: [nextIndex],
            insertion: { afterElementIndex: nextIndex }
          }
        };
        setExsurgeSelection(newSelection);
      }
    },
    [onUpdateNoteGabc, setExsurgeSelection]
  );

  const handleKeyDown = useCallback(
    e => {
      const score = scoreRef.current;
      if (MouseEventUtils.hasModKey(e)) {
        // check for Command / Ctrl keyboard shortcuts, e.g., document undo:
        let offset = 0;
        if (e.key === "z") {
          offset = e.shiftKey ? 1 : -1;
        } else if (e.key === "y" && !e.shiftKey) {
          offset = 1;
        }
        if (offset) {
          e.preventDefault();
          onUndoDocumentChange(offset);
        }
      }
      if (!elementSelection) return;
      const insertion = elementSelection.insertion,
        [, leftRight, upDown, note, bar, backspace, enter, spaceBar, tab] =
          /^(?:Arrow(?:(Left|Right)|(Up|Down))|([a-mA-M])|([:;,`])|(Backspace|Delete)|(Enter)|( )|(Tab))$/.exec(
            e.key
          ) || [];
      let selectionNotes = selectedIndices.map(i =>
        i < 0 ? score.startingClef : score.notes[i]
      );

      if (upDown) {
        // only for clef, since note modifications are handled by the note panel:
        if (selectionNotes.every(elem => elem instanceof exsurge.Clef)) {
          let offset = upDown === "Up" ? 1 : -1;
          onUpdateNoteGabc(
            selectionNotes,
            selectionNotes.map(clef =>
              (clef.sourceGabc || "c4").replace(
                /^([cf]b?)([1-4])/,
                (whole, clef, position) => {
                  let newPosition = parseInt(position) + offset;
                  if (newPosition < 1 || newPosition > 4)
                    newPosition = position;
                  return clef + newPosition;
                }
              )
            )
          );
          e.preventDefault();
        }
      } else if (leftRight) {
        // new selection / updated selection
        let offset = leftRight === "Left" ? -1 : 1,
          insertionOffset = Math.max(0, offset);
        if (e.shiftKey) {
          // Shift down as well: expand / contract selection
          let anchor = elementSelection.anchor,
            start = selectedIndices[anchor],
            elementIndex = Math.min(
              score.notes.length - 1,
              Math.max(0, selectedIndices[selectedIndices.length - 1] + offset)
            );
          if (typeof anchor !== "number") {
            if (selectedIndices.length) {
              anchor = selectedIndices.length - 1;
              start = selectedIndices[anchor];
            } else if (insertion) {
              anchor = 0;
              start =
                (insertion.afterElementIndex >= 0
                  ? insertion.afterElementIndex
                  : -1) + insertionOffset;
              elementIndex = start;
            }
          }
          let newIndices = selectedIndices.slice(0, anchor),
            newSelection = { indices: newIndices },
            diff = elementIndex - start,
            increment = Math.round(diff / Math.abs(diff)),
            min = Math.min(start, elementIndex),
            max = Math.max(start, elementIndex);
          for (let i = start; i >= min && i <= max; i += increment) {
            let index = newIndices.indexOf(i);
            if (index >= 0) newIndices.splice(index, 1);
            newIndices.push(i);
          }
          newSelection.anchor = anchor;
          setExsurgeSelection({ element: newSelection });
        } else {
          // No shift key: move selection
          let insertionNote =
            insertion && score.notes[insertion.afterElementIndex];
          if (
            insertionOffset === 0 &&
            insertionNote instanceof exsurge.TextOnly
          ) {
            insertionOffset = -1;
          }
          let selectedIndex = insertion
            ? (insertionNote ? insertion.afterElementIndex : -1) +
              (selectedIndices.length ? offset : insertionOffset)
            : selectedIndices[selectedIndices.length - 1] + offset;
          let newSelection = {};
          if (selectedIndex >= 0 && selectedIndex < score.notes.length) {
            newSelection.indices = [selectedIndex];
            newSelection.anchor = 0;
            const newNote = score.notes[selectedIndex];
            if (newNote instanceof exsurge.TextOnly) {
              updateNoteGabcForTextOnly(selectedIndex, newNote, false);
            }
          } else {
            newSelection.indices = [];
            if (selectedIndex < 0) {
              newSelection.insertion = { chantLine: 0 };
            } else {
              newSelection.insertion = {
                afterElementIndex: score.notes.length - 1
              };
            }
          }
          setExsurgeSelection({ element: newSelection });
          e.preventDefault();
        }
      } else if (bar) {
        let barSelection =
          selectionNotes.length === 1 &&
          (selectionNotes[0].isDivider ||
            selectionNotes[0] instanceof exsurge.TextOnly) &&
          selectionNotes[0];
        if (insertion || barSelection) {
          e.preventDefault();
          // special case for ":" because it needs to toggle between a full bar and a double bar:
          if (barSelection) {
            let insertBar = bar;
            if (bar === ":" && barSelection instanceof exsurge.FullBar) {
              insertBar = "::";
            }
            onUpdateNoteGabc(selectionNotes, insertBar, null, {
              keepSelection: true
            });
          } else {
            onUpdateNoteGabc([], bar, insertion);
          }
        }
      } else if (note || backspace) {
        // new note, or removing notes:
        if (insertion && note) {
          // If there is an insertion cursor, we will add it in,
          onUpdateNoteGabc([], note, insertion);
        } else {
          if (backspace && insertion && !selectionNotes.length) {
            if (insertion.afterElementIndex >= 0)
              selectionNotes = [score.notes[insertion.afterElementIndex]];
          }
          if (selectionNotes.every(elem => elem instanceof exsurge.Clef)) {
            if (/[cfb]/i.test(note)) {
              let clefReplace =
                note === "b"
                  ? clef =>
                      clef.replace(
                        /^c(b?)([1-4])/,
                        (whole, flat, position) =>
                          `c${flat ? "" : "b"}${position}`
                      )
                  : clef => clef.replace(/^[cf]b?([1-4])/, `${note}$1`);
              // replace the clef
              onUpdateNoteGabc(
                selectionNotes,
                selectionNotes.map(clef => clefReplace(clef.sourceGabc || "c4"))
              );
            }
          } else {
            // when there is no insertion cursor, it will replace all currently selected notes.
            onUpdateNoteGabc(selectionNotes, note || "", null, {
              keepSelection: !!backspace
            });
          }
        }
        e.preventDefault();
      } else if (enter && !insertion) {
        // enter insertion mode at current cursor:
        let newSelection = { indices: [selectedIndices.pop()] };
        newSelection.insertion = {
          afterElementIndex: newSelection.indices[0]
        };
        setExsurgeSelection({ element: newSelection });
        e.preventDefault();
      } else if (spaceBar && !insertion) {
        // begin insertion at next neume
        const currentIndex = selectedIndices.pop(),
          notations = score.notations,
          currentNote = score.notes[currentIndex],
          currentNotation = currentNote.neume || currentNote;
        let i = notations.indexOf(currentNotation) + 1,
          notation = currentNotation;
        if (i === 0) {
          // the notation was not found
          console.warn(
            "currently selected notation not found:",
            currentNotation
          );
          return;
        }
        for (; i < notations.length; ++i) {
          notation = notations[i];
          if (notation.firstOfParentheses) break;
        }
        if (notation !== currentNotation && notation.firstOfParentheses) {
          // select this notation:
          const note = (notation.notes && notation.notes[0]) || notation,
            noteIndex = score.notes.indexOf(note);
          if (noteIndex < 0) {
            console.warn("Note not found", note);
          } else {
            setExsurgeSelection({ element: { indices: [noteIndex] } });
          }
        }
        e.preventDefault();
      } else if (insertion && (spaceBar || tab)) {
        e.preventDefault();
        let nextIndex = insertion.afterElementIndex + 1 || 0,
          nextNote = score.notes[nextIndex];
        if (spaceBar && nextNote instanceof exsurge.TextOnly) {
          updateNoteGabcForTextOnly(nextIndex, nextNote);
        } else {
          onUpdateNoteGabc([], ") (", insertion);
        }
      }
    },
    [
      elementSelection,
      selectedIndices,
      setExsurgeSelection,
      onUpdateNoteGabc,
      onUndoDocumentChange,
      updateNoteGabcForTextOnly
    ]
  );
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [handleKeyDown]);
  const clearSelectionAndPreventDefault = useCallback(e => {
    document.getSelection().removeAllRanges();
    document.activeElement.blur();
    e.preventDefault();
  }, []);
  const updateExsurgeSelectionFromMouse = useCallback(
    e => {
      const score = scoreRef.current;
      let target = e.target;
      while (
        target &&
        !target.hasAttribute("source-index") &&
        !target.hasAttribute("element-index") &&
        !target.hasAttribute("source-gabc")
      ) {
        target = target.parentElement;
        if (!target || /^svg$/i.test(target.tagName)) {
          // clear the selection if the click was outside the SVG
          setExsurgeSelection({});
          return;
        }
      }
      for (
        var svg = target.parentElement;
        svg && !/^svg$/i.test(svg.tagName);

      ) {
        svg = svg.parentElement;
      }
      let classList = target.classList;
      if (classList.contains("ChantScore")) {
        setExsurgeSelection({});
        return;
      }

      let svgPoint = svg.createSVGPoint();
      svgPoint.x = e.clientX;
      svgPoint.y = e.clientY;
      var svgP = svgPoint.matrixTransform(
        target.parentElement.getScreenCTM().inverse()
      );

      if (classList.contains("chantLine")) {
        // clicked on the chant line, outside of a note, so we mark the insertion point as the last note to the left of the click
        let line = score.lines[target.getAttribute("element-index")];
        let notation = line.bisectNotationAtX(svgP.x);
        let element = notation;

        if (!notation) {
          // check if the first element is a text only, and select it:
          const firstElem = score.notations[line.notationsStartIndex];
          if (firstElem instanceof exsurge.TextOnly) {
            notation = firstElem;
          }
        }

        if (!notation) {
          // the insertion cursor should show at the beginning of the system
          // since they clicked to the left of the first note
          notation = line;
        } else if (notation.notes) {
          notation = notation.notes.slice(-1)[0];
        } else if (notation instanceof exsurge.TextOnly) {
          // for text only, we must find the first in the sequence of text only elements that was clicked:
          let notations = notation.score.notations;
          for (
            let i = notations.indexOf(notation) - 1;
            i >= 0 && notations[i] instanceof exsurge.TextOnly;
            --i
          ) {
            notation = notations[i];
          }
          // onUpdateNoteGabc must be called in case a set of parentheses needs to be added:
          let index = notation.elementIndex;
          updateNoteGabcForTextOnly(index, notation, false);
        } else {
          // in this case, the closest element was a barline
        }
        let elementSelection = {
          indices: [],
          insertion: {}
        };

        if (notation === line || element?.line !== line) {
          elementSelection.insertion.chantLine = line.elementIndex;
        }
        if (notation !== line) {
          elementSelection.insertion.afterElementIndex = notation.elementIndex;
        }

        setExsurgeSelection({ element: elementSelection });
        clearSelectionAndPreventDefault(e);
        return;
      }

      if (e.altKey) {
        let gabc = target.getAttribute("source-gabc") || "",
          index =
            parseInt(target.getAttribute("source-index")) +
            gabcHeaderLenRef.current,
          length = gabc.length;
        setGabcSourceSelection(index, length);
        clearSelectionAndPreventDefault(e);
        return;
      }

      // if (target.tagName === "text") {
      //   let textType;
      //   for (let className of classList) {
      //     textType = exsurge.TextTypesByClass[className];
      //     if (textType) break;
      //   }
      //   setExsurgeSelection({ text: [{ textType }] });
      //   return;
      // }

      let elementIndex = target.getAttribute("element-index");
      if (!elementIndex) return;

      elementIndex = parseInt(elementIndex);
      if (/^#Porrectus\d$/.test(target.href?.baseVal)) {
        // special case for porrectus
        let bbox = target.getBBox();
        if (svgP.x > bbox.x + bbox.width / 2) {
          ++elementIndex;
        }
      }

      if (MouseEventUtils.hasModKey(e)) {
        // toggle selection of current index:
        let selectionIndex =
          selectedIndices && selectedIndices.indexOf(elementIndex);
        if (selectionIndex >= 0) {
          let newSelection = elementSelection;
          newSelection.indices.splice(selectionIndex, 1);
          setExsurgeSelection(
            newSelection.indices.length ? { element: newSelection } : {}
          );
          clearSelectionAndPreventDefault(e);
        } else {
          let newSelection = {
            indices: (selectedIndices || []).concat(elementIndex)
          };
          newSelection.anchor = newSelection.indices.length - 1;
          setExsurgeSelection({
            element: newSelection
          });
          clearSelectionAndPreventDefault(e);
        }
      } else if (e.shiftKey) {
        // add to selection with all between this note and last anchor point (either the cursor, or the last single selection)
        if (elementSelection && typeof elementSelection.anchor === "number") {
          let anchor = elementSelection.anchor,
            newSelection = { indices: selectedIndices.slice(0, anchor) },
            start = selectedIndices[anchor],
            diff = elementIndex - start,
            increment = Math.round(diff / Math.abs(diff)),
            min = Math.min(start, elementIndex),
            max = Math.max(start, elementIndex);
          for (let i = start; i >= min && i <= max; i += increment) {
            if (!newSelection.indices.includes(i)) newSelection.indices.push(i);
          }
          newSelection.anchor = anchor;
          setExsurgeSelection({ element: newSelection });
          clearSelectionAndPreventDefault(e);
        }
      } else if (e.ctrlKey) {
        // on Mac ignore if just the ctrl key is down
        return;
      } else {
        let newSelection = { indices: [elementIndex] };
        newSelection.anchor = 0;
        if (e.type === "dblclick")
          newSelection.insertion = { afterElementIndex: elementIndex };
        setExsurgeSelection({ element: newSelection });
        clearSelectionAndPreventDefault(e);
      }
    },
    [
      elementSelection,
      selectedIndices,
      setExsurgeSelection,
      setGabcSourceSelection,
      clearSelectionAndPreventDefault,
      updateNoteGabcForTextOnly,
    ]
  );

  const checkForExsurgeTextSelection = useCallback(() => {
    let selection = getSelection();
    if (selection.rangeCount !== 1) return;
    let range = selection.getRangeAt(0),
      { anchorNode, focusNode, anchorOffset, focusOffset } = selection,
      { commonAncestorContainer, startContainer, endContainer } = range;
    // console.info(
    //   "New text selection:",
    //   selection.toString(),
    //   selection.anchorNode,
    //   selection.focusNode
    // );
    let commonAncestor = findCommonAncestor(
      anchorNode,
      focusNode,
      node => "text" === node.tagName
    );
    let setExtentToAnchorTextNode = !commonAncestor;
    commonAncestor = commonAncestor || anchorNode;
    if (
      commonAncestor &&
      (commonAncestor.nodeType === Node.TEXT_NODE ||
        /^(text|tspan)$/.test(commonAncestor.tagName))
    ) {
      const score = scoreRef.current;
      let element = commonAncestor,
        textTypeClasses = Object.keys(exsurge.TextTypesByClass),
        textTypeClass;
      while (
        element &&
        (!element.classList ||
          !(textTypeClass = Array.from(element.classList).find(className =>
            textTypeClasses.includes(className)
          )))
      ) {
        element = element.parentElement;
      }
      try {
        let textType = exsurge.TextTypesByClass[textTypeClass],
          source =
            textType &&
            (textType.getFromSvgElem || textType.getFromScore)(score, element);
        if (source) {
          if (setExtentToAnchorTextNode) {
            selection.extend(
              element,
              startContainer === anchorNode ? element.childNodes.length : 0
            );
          }
          let offA = TextSelection.calculateCharOffsetFromAncestor(
              anchorNode,
              element,
              anchorOffset
            ),
            offF = TextSelection.calculateCharOffsetFromAncestor(
              focusNode,
              element,
              focusOffset
            ),
            start = Math.min(offA, offF),
            end = Math.max(offA, offF);
          setExsurgeSelection({
            text: [{ source, start, end }]
          });
        }
      } catch (e) {}
    }
  }, [setExsurgeSelection]);
  useEffect(() => {
    document.addEventListener(
      "selectionchange",
      checkForExsurgeTextSelection,
      false
    );

    return () => {
      document.removeEventListener(
        "selectionchange",
        checkForExsurgeTextSelection,
        false
      );
    };
  }, [checkForExsurgeTextSelection]);

  const onScoreUpdate = props.onScoreUpdate;
  const handleScoreUpdate = useCallback(
    (score, gabcHeaderLen) => {
      scoreRef.current = score;
      gabcHeaderLenRef.current = gabcHeaderLen;
      if (typeof onScoreUpdate === "function")
        onScoreUpdate(score, gabcHeaderLen);
    },
    [onScoreUpdate]
  );

  const exsurgeStyle = useMemo(
    () => ({
      padding: doc.margin * props.zoom + (doc.units || 'in'),
      height: doc.height * props.zoom + (doc.units || 'in'),
      width: doc.width * props.zoom + (doc.units || 'in')
    }),
    [doc.height, doc.margin, doc.units, doc.width, props.zoom]
  );

  const handleExsurgeKeyDown = useCallback(e => e.preventDefault, []);

  let minWidth = doc.width * props.zoom * ChantDocument.getPixelMultiplier(doc.units) + 1 * 250 + 2 * 28;

  return (
    <div
      className="EditorPane MainPane flex-fill d-flex flex-column"
      onMouseDown={updateExsurgeSelectionFromMouse}
    >
      {showHeader && (
        <header className="EditorPane-header d-flex d-print-none">
          <div className="flex-fill">
            <h5 className="m-0">
              <Badge
                variant="primary"
                className="pl-2 shadowed-badge font-weight-normal form-inline"
              >
                <FontAwesomeIcon icon={faFile} />
                &nbsp;&nbsp;
                <Form.Control
                  plaintext
                  defaultValue={doc.name}
                  placeholder="Untitled Document"
                  className="text-light w-auto"
                  onKeyDown={handleFileNameKeyDown}
                />
              </Badge>
            </h5>
          </div>
          <Form.Switch
            className="fa-code"
            id="switchCode"
            checked={showingCode}
            label=""
            onChange={e => setShowingCode(e.target.checked)}
          />
        </header>
      )}
      {showingCode && (
        <div className="EditorCode-body d-print-none">
          <AceEditor
            ref={aceRef}
            wrapEnabled
            height="200px"
            width="100%"
            theme="xcode"
            mode="abc"
            value={doc.gabc}
            focus
            onChange={gabc => {
              props.onChange(gabc);
            }}
            commands={gabcAceCustomCommands}
            
          />
        </div>
      )}
      {/* <pre className="EditorCode-body" style={{whiteSpace: "pre-wrap"}}>
          {JSON.stringify(props.document, true, "  ")}
        </pre> */}
      <SvgDefs />
      <style>{`@media (min-width: ${minWidth}px) {
        .EditorPane-body {
          justify-content: center;
        }
      }`}</style>
      <div
        onDoubleClick={updateExsurgeSelectionFromMouse}
        className="EditorPane-body flex-fill d-flex flex-wrap align-content-start"
      >
        <Exsurge
          contentEditable
          onKeyDown={handleExsurgeKeyDown}
          className="ChantSheet"
          id="ExsurgeChant"
          style={exsurgeStyle}
          zoom={props.zoom}
          onScoreUpdate={handleScoreUpdate}
          selection={exsurgeSelection}
          alignment={doc.alignment}
          gabc={doc.gabc}
          useDropCap={doc.dropCap}
          font={doc.font}
          contextCreated={contextCreated}
          mergeAnnotationWithTextLeft={false}
          {...ChantDocument.getCalculatedProps(doc)}
        />
      </div>
    </div>
  );
}

export default EditorPane;
